import { useMemo } from "react";
import { Headphones, Volume2, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MASTERING_TARGET_LABELS, clamp, fmtLufs, fmtDb } from "./dawHelpers";
import LedLevelMeter from "@/components/suno/LedLevelMeter";

function MasteringStat({ label, before, after, fmt }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 px-2 py-1.5">
      <p className="font-mono text-[9px] uppercase tracking-wider text-white/45">{label}</p>
      <div className="mt-0.5 flex items-baseline gap-1.5">
        <span className="font-mono text-[11px] text-white/55 line-through decoration-white/30">{fmt(before)}</span>
        <span className="font-mono text-[12px] font-bold text-emerald-300">{fmt(after)}</span>
      </div>
    </div>
  );
}

function MasteringResults({ stats, onReset }) {
  if (!stats) return null;
  return (
    <div data-testid="mastering-results" className="mt-3 rounded-2xl border border-emerald-400/30 bg-emerald-500/[0.04] p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-300">
          Mastered · {stats.target.label}
        </p>
        <button
          data-testid="mastering-reset-btn"
          onClick={onReset}
          className="rounded-md bg-white/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/70 hover:bg-white/20"
        >
          Bypass
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <MasteringStat label="Loudness" before={stats.before.lufs} after={stats.after.lufs} fmt={fmtLufs} />
        <MasteringStat label="True Peak" before={stats.before.truePeakDb} after={stats.after.truePeakDb} fmt={fmtDb} />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <MasteringStat label="Low"  before={stats.before.tilt.low}  after={stats.after.tilt.low}  fmt={fmtDb} />
        <MasteringStat label="Mid"  before={stats.before.tilt.mid}  after={stats.after.tilt.mid}  fmt={fmtDb} />
        <MasteringStat label="High" before={stats.before.tilt.high} after={stats.after.tilt.high} fmt={fmtDb} />
      </div>
    </div>
  );
}

export default function MasterBusCard({ masterVol, ceiling, masterPeak, onVolChange, onCeilingChange, onMaster, onMasterAndBounce, busy, masteringStats, masteringTarget, onTargetChange, onMasteringReset }) {
  // iter-256 — Boost the engine's masterPeak by 1.4× so a normal mix
  // hovers around the green/amber boundary on the 16-LED ladder rather
  // than bottom-3 (the engine's RMS-ish peak typically sits near 0.5
  // for a healthy mix). Boost matches the pre-iter-256 gradient bar.
  const scaledPeak = useMemo(() => clamp((masterPeak || 0) * 1.4, 0, 1), [masterPeak]);
  return (
    <Card data-testid="master-bus-card" className="glass-card rounded-3xl border-0 text-white">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <Headphones className="h-5 w-5 text-emerald-300 drop-shadow-[0_0_10px_rgba(52,211,153,0.55)]" />
          <h2 className="font-display text-xl font-semibold tracking-tight">Master Bus</h2>
        </div>
        {/* iter-256 — Replaces the thin continuous gradient bar with a
            proper 16-segment hardware-style LED ladder. Sits in the
            same slot above the volume slider but is now wide enough +
            tall enough to actually READ during a mix. Clips into a
            pulsing red halo (.led-meter[data-clipping="1"]) when the
            engine's masterPeak hits the top red zone so producers see
            the warning BEFORE bouncing. */}
        <div className="mb-4 master-led-meter-wrap">
          <LedLevelMeter
            peak={scaledPeak}
            playing
            orientation="horizontal"
            segments={16}
            testid="master-led-meter"
            ariaLabel="Master bus level — green normal, amber loud, red clipping"
          />
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1 flex justify-between font-mono text-[10px] uppercase tracking-wider text-white/55">
              <span>Master Volume</span><span>{masterVol}</span>
            </label>
            <input data-testid="master-volume" type="range" min="0" max="100" value={masterVol} onChange={onVolChange} className="studio-fader w-full" style={{ accentColor: "#34d399" }} />
          </div>
          <div>
            <label className="mb-1 flex justify-between font-mono text-[10px] uppercase tracking-wider text-white/55">
              <span>Limiter Ceiling</span><span>{ceiling}</span>
            </label>
            <input data-testid="master-ceiling" type="range" min="0" max="100" value={ceiling} onChange={onCeilingChange} className="studio-fader w-full" style={{ accentColor: "#e879f9" }} />
          </div>
        </div>

        {/* Mastering target preset */}
        <div className="mt-4">
          <p className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-white/55">Master target</p>
          <div className="grid grid-cols-4 gap-1.5">
            {MASTERING_TARGET_LABELS.map(({ key, label, sub }) => (
              <button
                key={`mtarget-${key}`}
                data-testid={`mastering-target-${key}`}
                onClick={() => onTargetChange(key)}
                disabled={busy}
                className={`rounded-lg border px-1.5 py-1.5 text-center transition disabled:opacity-50 ${
                  masteringTarget === key
                    ? "border-emerald-400/60 bg-emerald-500/15 text-white"
                    : "border-white/10 bg-black/30 text-white/70 hover:bg-black/40"
                }`}
              >
                <p className="text-[11px] font-semibold">{label}</p>
                <p className="font-mono text-[9px] tracking-wider text-white/55">{sub}</p>
              </button>
            ))}
          </div>
        </div>

        <Button data-testid="ai-master-btn" onClick={onMaster} className="mt-4 w-full rounded-2xl bg-gradient-to-r from-fuchsia-500 to-sky-500 text-white hover:opacity-90" disabled={busy}>
          <Volume2 className="mr-2 h-4 w-4" />{busy === "mastering" ? "Mastering…" : "AI Master Track"}
        </Button>

        {/* One-click "ready for streaming" combo: AI master → bounce → download.
            Sits below the regular master button so users who want to A/B the
            mastering recipe first still have the original workflow available. */}
        <Button
          data-testid="master-bounce-btn"
          onClick={onMasterAndBounce}
          className="mt-2 w-full rounded-2xl border border-emerald-300/40 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25"
          disabled={busy}
        >
          <Sparkles className="mr-2 h-4 w-4" />
          {busy === "master-and-bounce" ? "Mastering + bouncing…" : `Master + Bounce · ${masteringTarget?.toUpperCase() || "STREAMING"}`}
        </Button>

        <MasteringResults stats={masteringStats} onReset={onMasteringReset} />
      </CardContent>
    </Card>
  );
}
